import {
	EthnicityOptions,
	getPatientContactsOptions,
	getPatientDemographicsOptions,
	InsuranceTypeEnum,
	RaceOptions,
	type PlanOfCareReviewViewModel
} from '@generated';
import { SmallSection, SmallSectionFieldRow } from '~/shared/section/SmallSection';
import { useSuspenseQuery } from '@tanstack/react-query';
import { formatPhoneNumber } from '~/util/functions';
import { getAge } from '~/util/date';

export function DemographicsTab({ review }: { review: PlanOfCareReviewViewModel }) {
	const { data } = useSuspenseQuery(getPatientDemographicsOptions(review.signature.certificationPeriod.stayId));
	const { data: contacts } = useSuspenseQuery(getPatientContactsOptions(review.signature.certificationPeriod.stayId));
	const patient = review.data.patient;
	const addresses = review.data.address;
	const races = data.raceIds
		.map(x => RaceOptions.find(r => r.id === x)?.label)
		.filter(Boolean)
		.join(', ');
	const ethnicities = data.ethnicityIds
		.map(x => EthnicityOptions.find(r => r.id === x)?.label)
		.filter(Boolean)
		.join(', ');
	const medicarePayor = review.data.insurance.payors.find(x => x.order === 1 && x.insuranceTypeId === InsuranceTypeEnum.Medicare);
	const medicaidPayor = review.data.insurance.payors.find(x => x.order === 1 && x.insuranceTypeId === InsuranceTypeEnum.Medicaid);
	const emergencyContact = contacts.contacts.find(x => x.emergencyContact);
	const caregiver = contacts.contacts.find(x => x.caregiver);
	return (
		<div className="space-y-3">
			<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
				<SmallSection title="Patient Identity">
					<dl className="grid grid-cols-2 gap-x-6 gap-y-3 p-4">
						<SmallSectionFieldRow label="Last Name" value={patient.lastName} />
						<SmallSectionFieldRow label="First Name" value={patient.firstName} />
						<SmallSectionFieldRow label="Middle Name / Initial" value={patient.middleInitial || '—'} />
						<SmallSectionFieldRow label="Suffix" value={patient.suffix || '—'} />
						<SmallSectionFieldRow label="Date of Birth" value={patient.dateOfBirth.toLocaleDateString()} />
						<SmallSectionFieldRow label="Age" value={`${getAge(patient.dateOfBirth)} years`} />
						<SmallSectionFieldRow label="Gender" value={patient.genderLabel} />
						<SmallSectionFieldRow label="Marital Status" value={data.maritalStatus} />
						<SmallSectionFieldRow label="Race" value={races} />
						<SmallSectionFieldRow label="Ethnicity" value={ethnicities} />
						<SmallSectionFieldRow label="Preferred Language" value={data.primaryLanguageLabel} />
					</dl>
				</SmallSection>
				<SmallSection title="Insurance Identifiers">
					<dl className="grid grid-cols-2 gap-x-6 gap-y-3 p-4">
						<SmallSectionFieldRow label="SSN (masked)" value={patient.socialSecurityNumber} />
						<SmallSectionFieldRow label="Medicare Beneficiary ID" value={medicarePayor ? medicarePayor.memberId : 'N/A'} />
						<SmallSectionFieldRow label="Medicaid ID" value={medicaidPayor ? medicaidPayor.memberId : 'N/A'} />
					</dl>
				</SmallSection>
			</div>

			<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
				<SmallSection title="Contact Information">
					<dl className="grid grid-cols-2 gap-x-6 gap-y-3 p-4">
						<SmallSectionFieldRow label="Street Address" value={addresses.visitAddress.addressOne} span />
						<SmallSectionFieldRow label="City" value={addresses.visitAddress.city} />
						<SmallSectionFieldRow label="State" value={addresses.visitAddress.state} />
						<SmallSectionFieldRow label="ZIP Code" value={addresses.visitAddress.zipCode} />
						<SmallSectionFieldRow label="Primary Phone" value={formatPhoneNumber(data.mainPhoneNumber)} />
						<SmallSectionFieldRow
							label="Alt. Phone"
							value={data.alternatePhoneNumber ? formatPhoneNumber(data.alternatePhoneNumber) : '-'}
						/>
						<SmallSectionFieldRow label="Email" value={data.emailAddress || '—'} />
					</dl>
				</SmallSection>
				<SmallSection title="Living Situation">
					<dl className="grid grid-cols-1 gap-y-3 p-4">
						<SmallSectionFieldRow label="Location of Care" value={addresses.locationOfCareLabel} />
						<div className="border-t border-gray-50 pt-2">
							<p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Emergency Contact</p>
							{!emergencyContact ? (
								<span className="text-xs text-gray-500">No emergency contact recorded</span>
							) : (
								<dl className="grid grid-cols-3 gap-3">
									<SmallSectionFieldRow label="Name" value={emergencyContact.name} />
									<SmallSectionFieldRow label="Relationship" value={emergencyContact.relationshipLabel} />
									<SmallSectionFieldRow label="Phone" value={formatPhoneNumber(emergencyContact.mainPhoneNumber)} />
								</dl>
							)}
						</div>
						{caregiver && (
							<div className="border-t border-gray-50 pt-2">
								<p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Primary Caregiver</p>
								<dl className="grid grid-cols-2 gap-3">
									<SmallSectionFieldRow label="Name" value={caregiver.name} />
									<SmallSectionFieldRow label="Relationship" value={caregiver.relationshipLabel} />
									<SmallSectionFieldRow label="Phone" value={formatPhoneNumber(caregiver.mainPhoneNumber)} />
									<SmallSectionFieldRow label="Availability" value={caregiver.scheduleAvailability ?? '-'} />
								</dl>
							</div>
						)}
					</dl>
				</SmallSection>
			</div>
		</div>
	);
}
